import { AxiosError, isAxiosError } from 'axios'
import { ErrorOption, FieldValues, Path, UseFormSetError } from 'react-hook-form'

/**
 * Allowlist entry for {@link setErrorsFromApiError}. Either an exact form path (`'sendTo'`,
 * `'config.points'`) which collapses any deeper API segments onto that field, or the same path
 * with a `.*` suffix (`'config.milestones.*'`) which keeps the deeper API segments and forwards
 * them to react-hook-form so per-item fields (e.g. `useFieldArray` entries) receive their own error.
 */
export type AllowlistEntry<T extends FieldValues> = Path<T> | `${Path<T>}.*`

/**
 * Turns API errors into react-hook-form `setError` calls so users see messages on the form (or on `root`).
 *
 * **What happens**
 *
 * - **Validation errors** — When the body says `ValidationError`, each item in `details` can target a field.
 *   You pass which fields are allowed (`fields`). If the backend `path` matches an allowed prefix, the error
 *   is shown on that field; otherwise it goes to `root`. Same field twice: messages are merged the usual RHF way.
 * - **Other API errors** — One message on `root` from `error` / `msg`.
 * - **Anything else** — A generic “something went wrong” on `root`.
 *
 * **Allowlist (`fields`)** — Dotted form paths like `email` or `config.milestones`. Only used for validation
 * details: the backend sends `path` as separate pieces; we check whether your string matches the beginning of
 * that list. Two flavors per entry:
 *
 * - **Exact entry** (e.g. `'sendTo'`, `'config.points'`) — collapses any deeper API segments onto the matched
 *   field, so `['sendTo', 2, 'id']` surfaces as `setError('sendTo', ...)`. Use this when the form has a single
 *   field/Controller covering an array (e.g. multi-select chips) so the error reaches the rendered field.
 * - **Wildcard entry** (e.g. `'config.milestones.*'`) — joins every API path segment with dots, so
 *   `['config','milestones','0','points']` becomes `setError('config.milestones.0.points', ...)`. Use this
 *   when nested fields (e.g. `useFieldArray` items) render their own per-item error and need the deep target.
 *
 * Use `[]` if you want every validation line on `root` instead.
 *
 * @param e Usually an Axios error with `response.data`; plain `Error` falls through to the generic root case.
 * @param setError From `useForm`.
 * @param fields Allowlist of form paths; see above. Wildcard suffix `.*` opts into deep API paths.
 *
 * @example
 * ```ts
 * // Field-level: backend path ['email'] with allowlist ['email']
 * setErrorsFromApiError(err, setError, ['email'])
 *
 * // Multi-select on one Controller: deep API path collapses onto the matched prefix.
 * setErrorsFromApiError(err, setError, ['sendTo']) // path ['sendTo',2,'id'] → setError('sendTo', ...)
 *
 * // useFieldArray items: wildcard entry preserves per-item paths.
 * setErrorsFromApiError(err, setError, ['config.milestones.*'])
 * // path ['config','milestones','0','points'] → setError('config.milestones.0.points', ...)
 *
 * // Mixed in a single call: collapse some, deep on others.
 * setErrorsFromApiError(err, setError, ['name', 'teamIds', 'config.milestones.*'])
 *
 * // No matching allowlist entry → error on 'root'
 * ```
 */
export const setErrorsFromApiError = <T extends FieldValues>(
  e: Error | AxiosError,
  setError: UseFormSetError<T>,
  fields: AllowlistEntry<T>[],
) => {
  // Local field level map of encountered errors
  const errorsByField = new Map<Path<T> | 'root', ErrorOption>()
  const addError = (field: Path<T> | 'root', type: string, message: string) => {
    if (field === 'root') {
      // Root errors are singular and overwrite any previously set ones
      errorsByField.set(field, { type, message })
    } else {
      // Field errors are merged
      const existing = errorsByField.get(field)
      if (existing) {
        // Overwrite with the last error type/message
        existing.type = type
        existing.message = message
        // Accumulate multiple errors in types
        existing.types = { ...existing.types, [type]: message }
      } else {
        errorsByField.set(field, { types: { [type]: message }, type, message })
      }
    }
  }

  if (isAxiosError(e)) {
    if (e.response) {
      const { error, msg } = e.response.data

      if (error === 'ValidationError') {
        const details = e.response.data.details || []
        for (const item of details) {
          const { type, path = [], message } = item

          const validationErrorSegments = Array.isArray(path) ? path : []
          /**
           * Each allowlist entry is split on "."; we treat those pieces as a required prefix of
           * the API `path` array (same order, same segment values). Example:
           * - `name` matches path `['name']` only.
           * - `config.milestones` matches `['config','milestones']` and also deeper paths such as
           *   `['config','milestones','0','points']` so nested validation still maps to a field key.
           *
           * A trailing `.*` opts into "deep" mode: the entry's prefix is matched as above, but the
           * resulting field name is the full joined API path so per-item fields (e.g. useFieldArray
           * entries) receive their own error. Without `.*`, deeper segments are collapsed onto the
           * matched entry so a single Controller covering an array (e.g. multi-select chips at
           * `sendTo` for a path like `sendTo.2.id`) still surfaces the error.
           *
           * If no allowlist entry matches, the detail is attached to `root` instead of a named field.
           */
          const matched = fields
            .map(field => {
              const fieldStr = String(field)
              const isDeep = fieldStr.endsWith('.*')
              const base = isDeep ? fieldStr.slice(0, -2) : fieldStr
              return { base, isDeep, prefix: base.split('.') }
            })
            .find(({ prefix }) =>
              prefix.every((segment, i) => validationErrorSegments[i] === segment),
            )
          const fieldName = (() => {
            if (!matched) return undefined
            if (matched.isDeep) {
              return validationErrorSegments.join('.') as Path<T>
            }
            return matched.base as Path<T>
          })()
          addError(fieldName ?? 'root', type || 'custom', message || 'Unknown Error')
        }
      } else if (error) {
        addError('root', error, msg || error)
      }
    }
  }

  // Fallback to default error if we couldn't extract anything from payload
  if (!errorsByField.size) {
    addError('root', 'unknown', 'Oops. Something went wrong')
  }

  // Push final list of errors to form state
  for (const field of errorsByField.keys()) {
    const err = errorsByField.get(field)
    if (err) {
      setError(field, err)
    }
  }
}
